import type { Resource } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  KpiCard,
  NoPermissionState,
  Notice,
  PageHeader,
  ResourceDot,
  Skeleton,
  Toolbar,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  BedDouble,
  Ban,
  Coins,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan, useSession } from '@/features/auth/session';
import { ResourceFormDrawer } from '@/features/resources/ResourceFormDrawer';
import {
  amenitiesQueryOptions,
  archiveResource,
  resourceKeys,
  resourceTypesQueryOptions,
  resourcesQueryOptions,
  updateResourceStatus,
  type ResourcesFilters,
} from '@/features/resources/queries';
import { OPERATIONAL_STATUSES, statusBadgeTone, statusTone } from '@/features/resources/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

/** `?new=resource` ouvre directement le tiroir de création (palette ⌘K). */
export const Route = createFileRoute('/_app/resources/')({
  validateSearch: (search: Record<string, unknown>): { new?: 'resource' } =>
    search['new'] === 'resource' ? { new: 'resource' } : {},
  component: ResourcesPage,
});

function ResourcesPage() {
  const { t, i18n } = useTranslation(['resources', 'common', 'errors']);
  const search = Route.useSearch();
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const canRead = useCan('resource.read');
  const canCreate = useCan('resource.create');
  const canUpdate = useCan('resource.update');
  const canArchive = useCan('resource.archive');

  const propertyId = session.user.properties[0]?.id ?? '';

  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [typeCode, setTypeCode] = useState<string | undefined>(undefined);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [drawer, setDrawer] = useState<{ open: boolean; resource?: Resource }>({ open: false });
  const [toArchive, setToArchive] = useState<Resource | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.length >= 2 ? term : ''), 200);
    return () => window.clearTimeout(timer);
  }, [term]);

  // La palette de commandes ouvre le tiroir puis l'URL est nettoyée.
  useEffect(() => {
    if (search.new !== 'resource') return;
    setDrawer({ open: true });
    void navigate({ to: '/resources', search: {}, replace: true });
  }, [search.new, navigate]);

  const scopedPropertyId = useScopedPropertyId();
  const filters: ResourcesFilters = {
    ...(scopedPropertyId ? { propertyId: scopedPropertyId } : {}),
    ...(debounced ? { q: debounced } : {}),
    ...(typeCode ? { typeCode } : {}),
    ...(includeInactive ? { includeInactive: true } : {}),
  };

  const query = useInfiniteQuery({ ...resourcesQueryOptions(filters), enabled: canRead });
  const typesQuery = useQuery({ ...resourceTypesQueryOptions(), enabled: canRead });
  const amenitiesQuery = useQuery({ ...amenitiesQueryOptions(), enabled: canCreate || canUpdate });
  const allResourcesQuery = useInfiniteQuery({
    ...resourcesQueryOptions({}),
    enabled: canCreate || canUpdate,
  });

  const statusMutation = useMutation({
    mutationFn: ({
      resource,
      status,
    }: {
      resource: Resource;
      status: Resource['operationalStatus'];
    }) => updateResourceStatus(resource.id, status, resource.version),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(
        t('resources:toasts.statusChanged', {
          status: t(`resources:status.${updated.operationalStatus}`),
        }),
      );
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const archiveMutation = useMutation({
    mutationFn: (resource: Resource) => archiveResource(resource.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(t('resources:toasts.archived'));
      setToArchive(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const resources = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const active = resources.filter((resource) => resource.isActive);
  const parents = active.filter((resource) => !resource.parentResourceId);
  const childCount = active.length - parents.length;
  const totalCapacity = parents.reduce((sum, resource) => sum + resource.capacityMax, 0);
  const unavailable = active.filter(
    (resource) =>
      resource.operationalStatus === 'MAINTENANCE' || resource.operationalStatus === 'BLOCKED',
  ).length;
  const lodgingRates = active
    .filter((resource) => resource.category === 'LODGING' && resource.defaultRate)
    .map((resource) => resource.defaultRate!.price);
  const averageRate =
    lodgingRates.length > 0
      ? Math.round(lodgingRates.reduce((sum, price) => sum + price, 0) / lodgingRates.length)
      : 0;

  // Les sous-ressources s'affichent juste sous leur parent.
  const grouped = parents.flatMap((parent) => [
    parent,
    ...active.filter((resource) => resource.parentResourceId === parent.id),
  ]);
  const orphans = active.filter(
    (resource) =>
      resource.parentResourceId && !parents.some((p) => p.id === resource.parentResourceId),
  );
  const displayed = [...grouped, ...orphans];

  const openCreate = () => setDrawer({ open: true });

  return (
    <Container>
      <PageHeader
        title={t('resources:title')}
        subtitle={t('resources:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('resources:title') },
        ]}
        actions={
          <Can permission="resource.create">
            <Button variant="primary" onClick={openCreate}>
              <Plus className="ico" aria-hidden="true" />
              {t('resources:create')}
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <div className="grid grid-4">
          <KpiCard
            tone="violet"
            icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
            label={t('resources:kpi.total')}
            value={parents.length}
            loading={query.isPending}
            footer={t('resources:kpi.totalFoot', { count: childCount })}
          />
          <KpiCard
            tone="blue"
            icon={<Users className="ico ico-16" aria-hidden="true" />}
            label={t('resources:kpi.capacity')}
            value={totalCapacity}
            loading={query.isPending}
            footer={t('resources:kpi.capacityFoot')}
          />
          <KpiCard
            tone="amber"
            icon={<Ban className="ico ico-16" aria-hidden="true" />}
            label={t('resources:kpi.blocked')}
            value={unavailable}
            loading={query.isPending}
            footer={t('resources:kpi.blockedFoot')}
          />
          <KpiCard
            tone="green"
            icon={<Coins className="ico ico-16" aria-hidden="true" />}
            label={t('resources:kpi.averageRate')}
            value={formatAmount(averageRate, i18n.language)}
            loading={query.isPending}
            footer={t('resources:kpi.averageRateFoot')}
          />
        </div>

        <Card>
          <CardHeader
            title={t('resources:title')}
            icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
            actions={
              <Badge tone="neutral">
                {t('resources:countLabel', { count: parents.length })} ·{' '}
                {t('resources:subCountLabel', { count: childCount })}
              </Badge>
            }
          />

          <Toolbar>
            <Input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('resources:searchPlaceholder')}
              aria-label={t('resources:searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 240, height: 32, fontSize: 'var(--fs-13)' }}
            />
            <ChipFilter active={typeCode === undefined} onClick={() => setTypeCode(undefined)}>
              {t('resources:filters.all')}
            </ChipFilter>
            {(typesQuery.data ?? []).map((type) => (
              <ChipFilter
                key={type.id}
                active={typeCode === type.code}
                onClick={() => setTypeCode(typeCode === type.code ? undefined : type.code)}
              >
                {type.name}
              </ChipFilter>
            ))}
            <span className="spacer" />
            <ChipFilter
              active={includeInactive}
              onClick={() => setIncludeInactive((value) => !value)}
            >
              {t('resources:filters.showInactive')}
            </ChipFilter>
          </Toolbar>

          <CardBody>
            {query.isPending ? (
              <div className="resource-grid">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} height={148} />
                ))}
              </div>
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : displayed.length === 0 ? (
              <EmptyState
                icon={<BedDouble className="ico ico-24" aria-hidden="true" />}
                title={
                  debounced || typeCode
                    ? t('resources:emptySearchTitle')
                    : t('resources:emptyTitle')
                }
                description={
                  debounced || typeCode ? t('resources:emptySearchBody') : t('resources:emptyBody')
                }
                action={
                  debounced || typeCode ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setTerm('');
                        setDebounced('');
                        setTypeCode(undefined);
                      }}
                    >
                      {t('common:actions.clearFilters')}
                    </Button>
                  ) : (
                    <Can permission="resource.create">
                      <Button variant="primary" onClick={openCreate}>
                        <Plus className="ico" aria-hidden="true" />
                        {t('resources:create')}
                      </Button>
                    </Can>
                  )
                }
              />
            ) : (
              <div className="resource-grid">
                {displayed.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    canUpdate={canUpdate}
                    canArchive={canArchive}
                    onEdit={() => setDrawer({ open: true, resource })}
                    onStatus={(status) => statusMutation.mutate({ resource, status })}
                    onArchive={() => setToArchive(resource)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Notice
          tone="violet"
          icon={<Layers className="ico ico-20 notice__ico" aria-hidden="true" />}
          title={t('resources:compositeNotice.title')}
        >
          <p className="mb-0 t-13">{t('resources:compositeNotice.body')}</p>
        </Notice>
      </div>

      <ResourceFormDrawer
        open={drawer.open}
        onOpenChange={(open) => setDrawer((current) => ({ ...current, open }))}
        propertyId={propertyId}
        types={typesQuery.data ?? []}
        amenities={amenitiesQuery.data ?? []}
        parentCandidates={allResourcesQuery.data?.pages.flatMap((page) => page.data) ?? []}
        resource={drawer.resource}
      />

      <Dialog open={toArchive !== null} onOpenChange={(open) => !open && setToArchive(null)}>
        {toArchive ? (
          <DialogContent
            title={t('resources:actions.archiveTitle', { name: toArchive.name })}
            description={t('resources:actions.archiveBody')}
            icon={<Archive className="ico ico-16" aria-hidden="true" />}
            iconTone="amber"
            closeLabel={t('common:actions.close')}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:actions.cancel')}</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  loading={archiveMutation.isPending}
                  onClick={() => archiveMutation.mutate(toArchive)}
                >
                  {t('resources:actions.archiveConfirm')}
                </Button>
              </>
            }
          />
        ) : null}
      </Dialog>
    </Container>
  );
}

interface ResourceCardProps {
  resource: Resource;
  canUpdate: boolean;
  canArchive: boolean;
  onEdit: () => void;
  onStatus: (status: Resource['operationalStatus']) => void;
  onArchive: () => void;
}

/** Carte d'inventaire : bordure haute colorée par statut (DESIGN_SYSTEM §3.5). */
function ResourceCard({
  resource,
  canUpdate,
  canArchive,
  onEdit,
  onStatus,
  onArchive,
}: ResourceCardProps) {
  const { t, i18n } = useTranslation(['resources', 'common']);
  const tone = statusTone(resource.operationalStatus);
  const isChild = Boolean(resource.parentResourceId);

  const floorLabel =
    resource.floor === null
      ? null
      : resource.floor === 0
        ? t('resources:card.floorGround')
        : resource.floor === 1
          ? t('resources:card.floorFirst')
          : t('resources:card.floorOther', { count: resource.floor });

  return (
    <article
      className={[
        'resource-card',
        `resource-card--status-${tone}`,
        isChild ? 'resource-card--child' : '',
        resource.isActive ? '' : 'resource-card--archived',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="resource-card__head">
        <ResourceDot tone={tone} label={t(`resources:status.${resource.operationalStatus}`)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="resource-card__title">
            <Link to="/resources/$resourceId" params={{ resourceId: resource.id }}>
              {resource.name}
            </Link>
          </div>
          <div className="t-12 muted">
            {resource.typeName}
            {resource.internalRef ? ` · ${resource.internalRef}` : ''}
            {floorLabel ? ` · ${floorLabel}` : ''}
          </div>
        </div>
        {canUpdate || canArchive ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton label={`${t('common:actions.openMenu')} — ${resource.name}`}>
                <MoreHorizontal className="ico ico-16" aria-hidden="true" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate ? (
                <>
                  <DropdownMenuItem onSelect={onEdit}>
                    <Pencil className="ico" aria-hidden="true" />
                    {t('resources:actions.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t('resources:status.change')}</DropdownMenuLabel>
                  {OPERATIONAL_STATUSES.map((status) => (
                    <DropdownMenuItem
                      key={status}
                      disabled={status === resource.operationalStatus}
                      onSelect={() => onStatus(status)}
                    >
                      <ResourceDot
                        tone={statusTone(status)}
                        label={t(`resources:status.${status}`)}
                      />
                      {t(`resources:status.${status}`)}
                    </DropdownMenuItem>
                  ))}
                </>
              ) : null}
              {canArchive && resource.isActive ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem danger onSelect={onArchive}>
                    <Archive className="ico" aria-hidden="true" />
                    {t('resources:actions.archive')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <div className="resource-card__meta">
        <span>
          {t('resources:card.capacity', {
            standard: resource.capacityStandard,
            max: resource.capacityMax,
          })}
        </span>
        {resource.bedrooms ? (
          <span>{t('resources:card.rooms', { count: resource.bedrooms })}</span>
        ) : null}
        {resource.surfaceM2 ? <span>{resource.surfaceM2} m²</span> : null}
      </div>

      <div className="resource-card__foot">
        <span className="resource-card__rate">
          {resource.defaultRate
            ? `${formatAmount(resource.defaultRate.price, i18n.language, resource.defaultRate.currencyCode)} ${t(`resources:rates.perUnit.${resource.defaultRate.pricingUnit}`)}`
            : t('resources:card.noRate')}
        </span>
        <span className="spacer" />
        {!resource.isActive ? (
          <Badge tone="neutral">{t('resources:card.archived')}</Badge>
        ) : resource.children.length > 0 ? (
          <Badge tone="violet">{t('resources:card.parent')}</Badge>
        ) : isChild ? (
          <Badge tone="neutral">{t('resources:card.child')}</Badge>
        ) : (
          <Badge tone={statusBadgeTone(resource.operationalStatus)} dot>
            {t(`resources:status.${resource.operationalStatus}`)}
          </Badge>
        )}
      </div>
    </article>
  );
}
